/* eslint-disable react/jsx-max-props-per-line */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Chip, Stack, Typography } from '@mui/material';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import { obterTextoVencimento, shouldShowExpirationChip } from '../Nsirs/utils';

const buildCodigoLine = ({ hasNsirId, codigo }) => {
  const trimmed = (codigo || '').trim();
  if (trimmed) return `NSIR ${trimmed}`;
  if (!hasNsirId) return 'Nova NSIR';
  return 'NSIR';
};

const ContextBar = ({ nsirData, codigo, equipamento }) => {
  const hasNsirId = Boolean(nsirData?.id);
  const codigoLine = useMemo(
    () => buildCodigoLine({ hasNsirId, codigo }),
    [hasNsirId, codigo]
  );

  const equipamentoLine = useMemo(() => {
    const trimmed = (equipamento || '').trim();
    return trimmed ? `Equipamento: ${trimmed}` : null;
  }, [equipamento]);

  const vencimentoChip = useMemo(() => {
    if (!nsirData?.vencimento || !shouldShowExpirationChip(nsirData)) return null;
    const dataVencimento = moment(nsirData.vencimento);
    const dataConclusao = nsirData.dataConclusao ? moment(nsirData.dataConclusao) : moment();
    return obterTextoVencimento(dataVencimento, dataConclusao);
  }, [nsirData]);

  const metaItems = useMemo(() => {
    const items = [];
    if (nsirData?.malhaNome) items.push({ label: 'Malha', value: nsirData.malhaNome });
    if (nsirData?.inspetorNome) items.push({ label: 'Inspetor', value: nsirData.inspetorNome });
    if (nsirData?.digitadorNome) items.push({ label: 'Digitador', value: nsirData.digitadorNome });
    return items;
  }, [nsirData]);

  if (!nsirData) return null;

  const showStatusRow = Boolean(nsirData.status || vencimentoChip);

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="h6"
        noWrap
        title={codigoLine}
        sx={{ fontSize: { xs: '1.05rem', sm: '1.15rem' }, fontWeight: 600, lineHeight: 1.3 }}
      >
        {codigoLine}
      </Typography>

      {equipamentoLine && (
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          title={equipamentoLine}
          sx={{ lineHeight: 1.3 }}
        >
          {equipamentoLine}
        </Typography>
      )}

      {showStatusRow && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ pt: equipamentoLine ? 0.25 : 0 }}
        >
          {nsirData.status && renderStatus({ value: nsirData.status, label: nsirData.statusNome })}
          {vencimentoChip && (
            <Chip label={vencimentoChip} size="small" color="warning" variant="outlined" />
          )}
        </Stack>
      )}

      {metaItems.length > 0 && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ pt: 0.25 }}>
          {metaItems.map((item) => (
            <Typography key={item.label} variant="caption" color="text.secondary" noWrap>
              <Typography component="span" variant="caption" fontWeight={600} color="text.primary">
                {item.label}:{' '}
              </Typography>
              {item.value}
            </Typography>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

ContextBar.propTypes = {
  nsirData: PropTypes.object,
  codigo: PropTypes.string,
  equipamento: PropTypes.string,
};

ContextBar.defaultProps = {
  nsirData: null,
  codigo: '',
  equipamento: '',
};

export default ContextBar;
